/**
 * IncompleteDispositionsNotifier — global "you have unfinished after-call
 * work" banner (Iter 210.7 · Feb 2026 · AriaOne "unify after-call-work").
 *
 * Why:
 *   The Sales Dialer disposition modal is now mandatory (Iter 210.7 ·
 *   Phase 2) and its "Pause — I'm on another call" button autosaves the
 *   draft rather than discarding it. Without a global surface, reps who
 *   paused mid-flow would never see the reminder unless they navigated
 *   back to that specific lead's Focus page (where ResumeDispositionsBanner
 *   surfaces the draft).
 *
 * Behavior:
 *   · Polls GET /api/dispositions?mine=true&incomplete=true&since_hours=48
 *     every 60s (and on focus / route change).
 *   · Renders a sticky bottom-right pill with count + per-row Resume
 *     button when drafts exist.
 *   · Resume navigates to the lead page and fires a
 *     `disposition:resume-request` window event; existing LeadFocus /
 *     SalesDialer wiring picks it up and re-opens the correct modal
 *     pre-filled with the draft.
 *   · Session-scoped dismiss (comes back on next login) so reps aren't
 *     nagged mid-flow but can't ignore the drafts long-term.
 *   · Suppresses itself when the currently-mounted modal is showing a
 *     draft (avoid duplicate surfacing).
 *
 * Safety:
 *   · Read-only — never mutates dispositions itself.
 *   · Silent on 401 / network errors so a Graph outage doesn't spam.
 *   · Zero effect if the rep has no incomplete drafts (renders null).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ChevronRight, X, Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";


const POLL_INTERVAL_MS = 60_000;
const SINCE_HOURS = 48;
const DISMISS_STORAGE_KEY = "iter210_7_incomplete_disp_dismissed_ts";
// Suppress for 15 min after dismiss so the rep isn't nagged mid-task.
const DISMISS_TTL_MS = 15 * 60_000;


export default function IncompleteDispositionsNotifier() {
  const { token, user } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);
  const lastFetchRef = useRef(0);

  // Session-scoped dismiss check
  useEffect(() => {
    try {
      const ts = Number(sessionStorage.getItem(DISMISS_STORAGE_KEY) || 0);
      if (ts && Date.now() - ts < DISMISS_TTL_MS) {
        setDismissed(true);
      }
    } catch { /* sessionStorage may not be available */ }
  }, []);

  const fetchDrafts = useCallback(async () => {
    if (!token) return;
    // Throttle — never fire two fetches within 5s (route change + focus
    // can both trigger simultaneously).
    if (Date.now() - lastFetchRef.current < 5_000) return;
    lastFetchRef.current = Date.now();
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dispositions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { mine: true, incomplete: true, since_hours: SINCE_HOURS, limit: 10 },
      });
      const rows = Array.isArray(r.data) ? r.data : [];
      setDrafts(rows);
    } catch {
      // Silent — a network blip should not spam the rep.
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch on mount + when user identity changes.
  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts, user?.id]);

  // Poll every 60s.
  useEffect(() => {
    pollRef.current = setInterval(fetchDrafts, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchDrafts]);

  // Re-fetch when the tab becomes visible (rep just came back).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") fetchDrafts(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [fetchDrafts]);

  // Listen for a global "disposition completed" event so we drop
  // stale drafts immediately without waiting for the next poll.
  useEffect(() => {
    const onDone = () => fetchDrafts();
    window.addEventListener("disposition:completed", onDone);
    return () => window.removeEventListener("disposition:completed", onDone);
  }, [fetchDrafts]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    } catch { /* ignore */ }
  }, []);

  const resume = useCallback((draft) => {
    if (!draft?.lead_id) return;
    // Navigate to lead page, then broadcast a resume-request event.
    // LeadFocus already listens for `webphone:call-ended` — we reuse
    // a similar pattern with `disposition:resume-request`.
    navigate(`/leads/${draft.lead_id}`);
    // Small delay so the target page has mounted its listener.
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("disposition:resume-request", {
        detail: {
          disposition_id: draft.id,
          lead_id: draft.lead_id,
          outcome: draft.outcome,
          notes: draft.notes,
        },
      }));
    }, 400);
  }, [navigate]);

  // Filter out any draft whose modal is currently on-screen. LeadFocus
  // stamps `window.__activeDispositionId` when its modal opens; we
  // suppress that row here to avoid double-surfacing.
  const visibleDrafts = useMemo(() => {
    const activeId = (typeof window !== "undefined" && window.__activeDispositionId) || null;
    return drafts.filter((d) => d.id !== activeId);
  }, [drafts]);

  if (dismissed || !visibleDrafts.length) return null;

  const count = visibleDrafts.length;

  return (
    <div
      data-testid="incomplete-dispositions-notifier"
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 60,
        width: expanded ? 340 : "auto",
        maxHeight: expanded ? "60vh" : undefined,
        background: "#fff",
        border: "0.5px solid #fcd34d",
        borderRadius: 12,
        boxShadow: "0 12px 32px rgba(120,53,15,0.18)",
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      {/* Collapsed pill state */}
      {!expanded && (
        <button
          data-testid="incomplete-disp-pill"
          onClick={() => setExpanded(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            background: "#fffbeb",
            color: "#92400e",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            width: "100%",
            fontFamily: "inherit",
          }}
        >
          <AlertCircle style={{ width: 14, height: 14 }} />
          {count} unfinished after-call log{count === 1 ? "" : "s"}
          <ChevronRight style={{ width: 12, height: 12, marginLeft: 4 }} />
        </button>
      )}

      {/* Expanded list */}
      {expanded && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              background: "#fffbeb",
              borderBottom: "0.5px solid #fef3c7",
            }}
          >
            <AlertCircle style={{ width: 14, height: 14, color: "#92400e" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e", flex: 1 }}>
              {count} unfinished after-call log{count === 1 ? "" : "s"}
            </span>
            {loading && <Loader2 style={{ width: 12, height: 12, color: "#92400e", animation: "spin 1s linear infinite" }} />}
            <button
              data-testid="incomplete-disp-collapse"
              onClick={() => setExpanded(false)}
              title="Collapse"
              style={{ background: "none", border: "none", color: "#92400e", cursor: "pointer", padding: 2 }}
            >
              <X style={{ width: 12, height: 12 }} />
            </button>
          </div>
          <div style={{ maxHeight: "42vh", overflowY: "auto" }}>
            {visibleDrafts.map((d) => (
              <button
                key={d.id}
                data-testid={`incomplete-disp-item-${d.id}`}
                onClick={() => resume(d)}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "10px 14px",
                  background: "#fff",
                  border: "none",
                  borderBottom: "0.5px solid #f5f5f4",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#fffbeb")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>
                  {d.lead_name || d.lead_practice || "Lead"}
                </div>
                <div style={{ fontSize: 10, color: "#64748b", display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {d.outcome || "pending"}
                  </span>
                  <span>·</span>
                  <span>
                    {(() => {
                      const t = d.updated_at || d.created_at;
                      if (!t) return "";
                      const mins = Math.round((Date.now() - new Date(t).getTime()) / 60_000);
                      if (mins < 1) return "just now";
                      if (mins < 60) return `${mins}m ago`;
                      return `${Math.round(mins / 60)}h ago`;
                    })()}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <button
            data-testid="incomplete-disp-dismiss"
            onClick={dismiss}
            style={{
              display: "block",
              width: "100%",
              padding: "8px 14px",
              background: "#fafaf9",
              border: "none",
              borderTop: "0.5px solid #f5f5f4",
              color: "#78716c",
              fontSize: 10,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Dismiss for 15 min
          </button>
        </>
      )}
    </div>
  );
}
